/**
 * Authentication Pages - Login & Register
 * ThePedigreeApp - South Africa's Pedigree Cat Platform
 */

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Cat, Mail, Lock, User, Phone, Eye, EyeOff, Building2, Award, Shield, CheckCircle, Clock } from 'lucide-react';
import { Card, Button, Input, Select, Alert } from '../components/shared/UIComponents';
import { useAuth, useForm } from '../hooks/useAuth';
import { signInWithGoogle } from '../firebase/auth';
import { cn } from '../utils/helpers';

const SA_PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
  'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape'
];

const REGISTRY_BODIES = [
  { value: 'SACC', label: 'SACC - South African Cat Council' },
  { value: 'CASA', label: 'CASA - Cat Association of Southern Africa' },
  { value: 'CFSA', label: 'CFSA - Cat Federation of Southern Africa' },
  { value: 'OTHER', label: 'Other / International Registry' }
];

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// =====================
// LOGIN PAGE
// =====================

export const LoginPage = () => {
  const { login, error: authError, setError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  const validate = (values) => {
    const errors = {};
    if (!values.email) errors.email = 'Email is required';
    if (!values.password) errors.password = 'Password is required';
    return errors;
  };

  const { values, errors, touched, isSubmitting, handleChange, handleBlur, handleSubmit } = useForm({ email: '', password: '' }, validate);

  const onSubmit = async (formValues) => {
    const user = await login(formValues.email, formValues.password);
    if (user?.role === 'breeder' && user?.breederStatus === 'pending_verification') {
      navigate('/verification-pending');
    } else {
      navigate(from, { replace: true });
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      const user = await signInWithGoogle('buyer');
      if (user.role === 'breeder') {
        if (user.breederStatus === 'pending_verification') {
          navigate('/verification-pending');
        } else if (user.breederStatus === 'approved') {
          navigate('/dashboard');
        } else {
          navigate('/marketplace');
        }
      } else {
        navigate('/marketplace');
      }
    } catch (error) {
      setError('Failed to sign in with Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 bg-feline-500 rounded-xl flex items-center justify-center">
            <Cat className="w-7 h-7 text-white" />
          </div>
          <span className="font-display font-bold text-2xl text-gray-900">
            The<span className="text-feline-500">Pedigree</span>App
          </span>
        </Link>
        <h1 className="text-center text-2xl font-display font-bold text-gray-900 mb-2">Welcome back</h1>
        <p className="text-center text-gray-500 mb-8">Sign in to your account</p>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          {authError && <Alert type="error" className="mb-4">{authError}</Alert>}

          <Button type="button" variant="outline" className="w-full mb-4 flex items-center justify-center gap-3" onClick={handleGoogleSignIn} loading={googleLoading}>
            <GoogleIcon />
            Continue with Google
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-500">or sign in with email</span></div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(onSubmit); }}>
            <div className="space-y-4">
              <Input label="Email" name="email" type="email" value={values.email} onChange={handleChange} onBlur={handleBlur} error={touched.email && errors.email} placeholder="you@example.com" icon={<Mail className="w-5 h-5" />} />
              <div className="relative">
                <Input label="Password" name="password" type={showPassword ? 'text' : 'password'} value={values.password} onChange={handleChange} onBlur={handleBlur} error={touched.password && errors.password} placeholder="••••••••" icon={<Lock className="w-5 h-5" />} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-9 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-feline-500" />
                  <span className="text-gray-600">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-feline-600 hover:text-feline-700 font-medium">Forgot password?</Link>
              </div>
              <Button type="submit" className="w-full" loading={isSubmitting}>Sign In</Button>
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-gray-500">
            Don't have an account? <Link to="/register" className="text-feline-600 hover:text-feline-700 font-medium">Register here</Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

// =====================
// REGISTER PAGE
// =====================

export const RegisterPage = () => {
  const { register, error: authError, setError } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [accountType, setAccountType] = useState('buyer');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showBreederForm, setShowBreederForm] = useState(false);
  const [breederDetails, setBreederDetails] = useState({ catteryName: '', registryBody: '', registryNumber: '', province: '' });

  const validate = (values) => {
    const errors = {};
    if (!values.displayName) errors.displayName = 'Name is required';
    if (!values.email) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errors.email = 'Invalid email';
    if (!values.password) errors.password = 'Password is required';
    else if (values.password.length < 8) errors.password = 'Min 8 characters';
    if (values.password !== values.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (accountType === 'breeder') {
      if (!breederDetails.catteryName) errors.catteryName = 'Required';
      if (!breederDetails.registryBody) errors.registryBody = 'Required';
      if (!breederDetails.registryNumber) errors.registryNumber = 'Required';
      if (!breederDetails.province) errors.province = 'Required';
    }
    return errors;
  };

  const { values, errors, touched, isSubmitting, handleChange, handleBlur, handleSubmit, setErrors } = useForm({ displayName: '', email: '', phone: '', password: '', confirmPassword: '' }, validate);

  const onSubmit = async (formValues) => {
    if (accountType === 'breeder') {
      if (!breederDetails.catteryName || !breederDetails.registryBody || !breederDetails.registryNumber || !breederDetails.province) {
        setError('Please complete all breeder verification fields');
        return;
      }
    }
    
    await register({
      displayName: formValues.displayName,
      email: formValues.email,
      phone: formValues.phone,
      password: formValues.password,
      role: accountType,
      ...(accountType === 'breeder' && breederDetails)
    });
    
    navigate(accountType === 'breeder' ? '/verification-pending' : '/marketplace');
  };

  const handleGoogleSignIn = async (role) => {
    if (role === 'breeder') {
      setAccountType('breeder');
      setShowBreederForm(true);
      return;
    }
    try {
      setGoogleLoading(true);
      const user = await signInWithGoogle(role);
      navigate(user.isNewUser ? '/marketplace' : (user.role === 'breeder' ? '/dashboard' : '/marketplace'));
    } catch (error) {
      setError('Failed to sign in with Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleBreederSignup = async () => {
    if (!breederDetails.catteryName || !breederDetails.registryBody || !breederDetails.registryNumber || !breederDetails.province) {
      setError('Please complete all verification fields');
      return;
    }
    try {
      setGoogleLoading(true);
      await signInWithGoogle('breeder', breederDetails);
      navigate('/verification-pending');
    } catch (error) {
      setError('Failed to sign in with Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 bg-feline-500 rounded-xl flex items-center justify-center">
            <Cat className="w-7 h-7 text-white" />
          </div>
          <span className="font-display font-bold text-2xl text-gray-900">
            The<span className="text-feline-500">Pedigree</span>App
          </span>
        </Link>
        <h1 className="text-center text-2xl font-display font-bold text-gray-900 mb-2">Create your account</h1>
        <p className="text-center text-gray-500 mb-8">Join South Africa's trusted pedigree cat platform</p>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button type="button" onClick={() => { setAccountType('buyer'); setShowBreederForm(false); }} className={cn('flex-1 py-2.5 text-sm font-medium rounded-md transition-colors', accountType === 'buyer' ? 'bg-white text-feline-600 shadow-sm' : 'text-gray-500')}>
              🛒 I'm a Buyer
            </button>
            <button type="button" onClick={() => setAccountType('breeder')} className={cn('flex-1 py-2.5 text-sm font-medium rounded-md transition-colors', accountType === 'breeder' ? 'bg-white text-feline-600 shadow-sm' : 'text-gray-500')}>
              🏠 I'm a Breeder
            </button>
          </div>

          {authError && <Alert type="error" className="mb-4">{authError}</Alert>}

          {accountType === 'breeder' && (
            <Alert type="warning" className="mb-4">
              <div className="flex items-start gap-2">
                <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Verification Required</p>
                  <p className="text-sm mt-1">To prevent scams, all breeders must provide valid registry details. Your account will be reviewed before listing.</p>
                </div>
              </div>
            </Alert>
          )}

          {!showBreederForm && (
            <>
              <Button type="button" variant="outline" className="w-full mb-4 flex items-center justify-center gap-3" onClick={() => handleGoogleSignIn(accountType)} loading={googleLoading}>
                <GoogleIcon />
                Continue with Google
              </Button>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-500">or register with email</span></div>
              </div>
            </>
          )}

          {showBreederForm && (
            <div className="mb-6">
              <Alert type="info" className="mb-4"><p className="text-sm">Complete verification to continue with Google.</p></Alert>
              <BreederVerificationFields details={breederDetails} onChange={setBreederDetails} errors={errors} />
              <div className="flex gap-3 mt-4">
                <Button type="button" variant="outline" onClick={() => setShowBreederForm(false)} className="flex-1">Back</Button>
                <Button type="button" onClick={handleGoogleBreederSignup} loading={googleLoading} className="flex-1"><GoogleIcon /> Continue</Button>
              </div>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-500">or use email</span></div>
              </div>
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(onSubmit); }}>
            <div className="space-y-4">
              <Input label="Full Name" name="displayName" value={values.displayName} onChange={handleChange} onBlur={handleBlur} error={touched.displayName && errors.displayName} placeholder="John Smith" icon={<User className="w-5 h-5" />} />
              <Input label="Email" name="email" type="email" value={values.email} onChange={handleChange} onBlur={handleBlur} error={touched.email && errors.email} placeholder="you@example.com" icon={<Mail className="w-5 h-5" />} />
              <Input label="Phone" name="phone" type="tel" value={values.phone} onChange={handleChange} placeholder="082 123 4567" icon={<Phone className="w-5 h-5" />} />

              {accountType === 'breeder' && !showBreederForm && (
                <BreederVerificationFields details={breederDetails} onChange={setBreederDetails} errors={errors} />
              )}

              <div className="relative">
                <Input label="Password" name="password" type={showPassword ? 'text' : 'password'} value={values.password} onChange={handleChange} onBlur={handleBlur} error={touched.password && errors.password} placeholder="Min. 8 characters" icon={<Lock className="w-5 h-5" />} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-9 text-gray-400">{showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
              </div>
              <Input label="Confirm Password" name="confirmPassword" type="password" value={values.confirmPassword} onChange={handleChange} onBlur={handleBlur} error={touched.confirmPassword && errors.confirmPassword} placeholder="••••••••" />

              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 mt-0.5 rounded border-gray-300 text-feline-500" required />
                <span className="text-sm text-gray-600">I agree to the <Link to="/terms" className="text-feline-600 hover:underline">Terms</Link> and <Link to="/privacy" className="text-feline-600 hover:underline">Privacy Policy</Link></span>
              </label>

              <Button type="submit" className="w-full" loading={isSubmitting}>
                {accountType === 'breeder' ? 'Submit for Verification' : 'Create Account'}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-gray-500">
            Already have an account? <Link to="/login" className="text-feline-600 hover:text-feline-700 font-medium">Sign in</Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

// =====================
// BREEDER VERIFICATION FIELDS
// =====================

const BreederVerificationFields = ({ details, onChange, errors }) => {
  const updateField = (field, value) => onChange({ ...details, [field]: value });

  return (
    <div className="space-y-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
      <h3 className="font-medium text-gray-900 flex items-center gap-2">
        <Award className="w-5 h-5 text-amber-600" />
        Breeder Verification Details
      </h3>
      <Input label="Cattery Name *" value={details.catteryName} onChange={(e) => updateField('catteryName', e.target.value)} placeholder="e.g., Whiskers Pride" error={errors?.catteryName} icon={<Building2 className="w-5 h-5" />} />
      <Select label="Registry Body *" value={details.registryBody} onChange={(e) => updateField('registryBody', e.target.value)} options={REGISTRY_BODIES} placeholder="Select registry" error={errors?.registryBody} />
      <Input label="Registry Number *" value={details.registryNumber} onChange={(e) => updateField('registryNumber', e.target.value)} placeholder="e.g., SACC-12345" error={errors?.registryNumber} icon={<Award className="w-5 h-5" />} />
      <Select label="Province *" value={details.province} onChange={(e) => updateField('province', e.target.value)} options={SA_PROVINCES.map(p => ({ value: p, label: p }))} placeholder="Select province" error={errors?.province} />
      <p className="text-xs text-amber-700">* We verify with the registry. Fraudulent applications = permanent ban.</p>
    </div>
  );
};

// =====================
// VERIFICATION PENDING PAGE
// =====================

export const VerificationPendingPage = () => (
  <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4">
    <div className="sm:mx-auto sm:w-full sm:max-w-md">
      <Card className="text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock className="w-8 h-8 text-amber-600" />
        </div>
        <h1 className="text-2xl font-display font-bold text-gray-900 mb-2">Verification Pending</h1>
        <p className="text-gray-600 mb-6">Your breeder account is being reviewed.</p>
        <div className="bg-gray-50 rounded-xl p-4 text-left mb-6">
          <h3 className="font-medium text-gray-900 mb-2">What happens next?</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-paw-500 mt-0.5" />We verify your registry details (1-2 days)</li>
            <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-paw-500 mt-0.5" />You'll receive an approval email</li>
            <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-paw-500 mt-0.5" />Then set up your cattery profile</li>
          </ul>
        </div>
        <Alert type="info"><p className="text-sm">Questions? <a href="mailto:verify@thepedigreeapp.co.za" className="underline">verify@thepedigreeapp.co.za</a></p></Alert>
        <div className="mt-6">
          <Link to="/marketplace"><Button variant="outline" className="w-full">Browse Marketplace</Button></Link>
        </div>
      </Card>
    </div>
  </div>
);

export default { LoginPage, RegisterPage, VerificationPendingPage };
